import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'motion/react';
import Dither from '@/components/dither';
import LoadingAnimation from '@/components/loading-animation';
import { VariableProximityText } from '@/components/variable-proximity';
import { Navbar } from '@/components/navbar';
import logoUrl from '@assets/vector/logo/lk-logo-white.svg';
import meImageUrl from '@assets/images/me.png';
import meGithubImageUrl from '@assets/images/me-github.png';
import ProfileCard from './components/profile-card';
import TerminalCard from './components/terminal-card';
import {
	IconBrandGithubFilled,
	IconBrandLinkedinFilled,
	IconBrandTwitterFilled,
	IconArrowDown,
} from '@tabler/icons-react';

const HAS_SEEN_LOADING_KEY = 'has_seen_loading_animation';

const getHasSeenLoading = (): boolean => {
	try {
		return sessionStorage.getItem(HAS_SEEN_LOADING_KEY) === 'true';
	} catch {
		return false;
	}
};

const setHasSeenLoading = (value: boolean): void => {
	try {
		if (value) {
			sessionStorage.setItem(HAS_SEEN_LOADING_KEY, 'true');
		} else {
			sessionStorage.removeItem(HAS_SEEN_LOADING_KEY);
		}
	} catch {}
};

export function App() {
	const hasSeenLoading = getHasSeenLoading();
	const [isLoaded, setIsLoaded] = useState(hasSeenLoading);
	const [activeSection, setActiveSection] = useState(0);

	const containerRef = useRef<HTMLDivElement>(null);

	const handleLoadingComplete = useCallback(() => {
		setHasSeenLoading(true);
		setIsLoaded(true);
	}, []);

	const scrollToSection = useCallback((index: number) => {
		const container = containerRef.current;

		if (!container) {
			return;
		}

		const sections = container.querySelectorAll('.snap-section');
		const targetSection = sections[index] as HTMLElement;

		if (targetSection) {
			container.scrollTo({
				top: targetSection.offsetTop,
				behavior: 'smooth',
			});
		}
	}, []);

	useEffect(() => {
		const container = containerRef.current;

		if (!container) {
			return;
		}

		const sections = container.querySelectorAll('.snap-section');

		const observerOptions = {
			root: container,
			threshold: 0.5,
		};

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const index = Array.from(sections).indexOf(entry.target);

					if (index !== -1) {
						setActiveSection(index);
					}
				}
			});
		}, observerOptions);

		sections.forEach((section) => observer.observe(section));

		return () => observer.disconnect();
	}, []);

	return (
		<>
			{!isLoaded && <LoadingAnimation onComplete={handleLoadingComplete} />}
			<a
				href="#main-content"
				className="sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:top-4 focus-visible:left-4 focus-visible:z-[100] focus-visible:px-4 focus-visible:py-2 focus-visible:bg-primary focus-visible:text-white focus-visible:rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
			>
				Skip to main content
			</a>
			<div
				ref={containerRef}
				className="scroll-container w-full h-dvh overflow-y-scroll"
				role="main"
				id="main-content"
				style={{ overscrollBehavior: 'none' }}
			>
				<motion.div
					initial={{ opacity: 0, y: -20 }}
					animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
					transition={{
						duration: 0.6,
						ease: [0.25, 0.46, 0.45, 0.94],
						delay: 0.2,
					}}
					className="hidden sm:flex fixed left-2 sm:left-4 md:left-8 top-0 mt-4 sm:mt-6 md:mt-8 z-50 pointer-events-auto items-center gap-3"
					aria-label="Social media links"
				>
					<a
						href="/github"
						target="_blank"
						rel="noopener noreferrer"
						aria-label="GitHub profile"
						className={`flex items-center justify-center transition-all duration-200 ease-out hover:scale-110 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
							activeSection === 1
								? 'text-primary focus-visible:ring-primary'
								: 'text-white focus-visible:ring-white'
						}`}
					>
						<IconBrandGithubFilled
							size={24}
							className="w-5 h-5 sm:w-6 sm:h-6"
						/>
					</a>
					<a
						href="/linkedin"
						target="_blank"
						rel="noopener noreferrer"
						aria-label="LinkedIn profile"
						className={`flex items-center justify-center transition-all duration-200 ease-out hover:scale-110 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
							activeSection === 1
								? 'text-primary focus-visible:ring-primary'
								: 'text-white focus-visible:ring-white'
						}`}
					>
						<IconBrandLinkedinFilled
							size={24}
							className="w-5 h-5 sm:w-6 sm:h-6"
						/>
					</a>
					<a
						href="/twitter"
						target="_blank"
						rel="noopener noreferrer"
						aria-label="Twitter profile"
						className={`flex items-center justify-center transition-all duration-200 ease-out hover:scale-110 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
							activeSection === 1
								? 'text-primary focus-visible:ring-primary'
								: 'text-white focus-visible:ring-white'
						}`}
					>
						<IconBrandTwitterFilled
							size={24}
							className="w-5 h-5 sm:w-6 sm:h-6"
						/>
					</a>
				</motion.div>

				<section
					className="snap-section w-full h-dvh relative"
					aria-label="Home section"
				>
					<Navbar
						isLoaded={isLoaded}
						activeSection={activeSection}
						onNavigate={(section) => scrollToSection(section)}
					/>
					<Dither
						waveColor={[21 / 255, 40 / 255, 252 / 255]}
						disableAnimation={false}
						enableMouseInteraction={true}
						mouseRadius={0.15}
						colorNum={2}
						pixelSize={3}
						waveAmplitude={0.55}
						waveFrequency={1.5}
						waveSpeed={0.04}
						logoExclusionRadius={0.05}
					/>
					<div
						className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-5"
						aria-hidden="true"
					>
						<img
							src={logoUrl}
							alt=""
							width={144}
							height={144}
							className="w-20 h-20 sm:w-24 sm:h-24 md:w-36 md:h-36"
							style={{ aspectRatio: '1 / 1' }}
							loading="eager"
							decoding="async"
							fetchPriority="high"
							aria-hidden="true"
						/>
					</div>
					<div className="absolute top-0 left-0 w-full h-full backdrop-blur-sm bg-black/5 pointer-events-none z-10" />
					<div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-black/40 via-black/20 to-transparent pointer-events-none z-15" />
					<div
						className="absolute bottom-0 left-0 sm:left-1/2 sm:-translate-x-1/2 w-full px-4 sm:px-4 pointer-events-none z-20 break-normal flex flex-col-reverse justify-end gap-3 sm:gap-4 md:gap-5"
						style={{
							maxHeight: '100dvh',
							paddingBottom: 'clamp(1rem, 4dvh, 2rem)',
						}}
					>
						<motion.div
							initial={{ opacity: 0 }}
							animate={isLoaded ? { opacity: 1 } : { opacity: 0 }}
							transition={{ duration: 0.6, ease: 'easeOut', delay: 0.5 }}
							className="hidden sm:flex justify-center"
						>
							<button
								onClick={() => scrollToSection(1)}
								className="flex items-center justify-center text-white/80 hover:text-white transition-colors duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-full p-2 pointer-events-auto"
								aria-label="Scroll to next section"
							>
								<IconArrowDown size={24} className="w-5 h-5 sm:w-6 sm:h-6" />
							</button>
						</motion.div>
						<motion.h1
							initial={{ opacity: 0, y: 24 }}
							animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
							transition={{
								duration: 0.6,
								ease: [0.25, 0.46, 0.45, 0.94],
								delay: 0.25,
							}}
							className="font-bold uppercase text-white leading-[0.85] font-sans text-[clamp(4rem,11vw,10.565rem)] sm:text-[clamp(3rem,8vw,10.565rem)] sm:leading-none text-left sm:text-center"
						>
							<span className="block sm:hidden">
								<VariableProximityText
									text="Lars"
									maxSpacing={0.12}
									minSpacing={-0.04}
									radius={200}
								/>
								<br />
								<VariableProximityText
									text="Kemper"
									maxSpacing={0.12}
									minSpacing={-0.04}
									radius={200}
								/>
							</span>
							<span className="hidden sm:block whitespace-nowrap">
								<VariableProximityText
									text="Lars Kemper"
									maxSpacing={0.12}
									minSpacing={-0.04}
									radius={200}
								/>
							</span>
						</motion.h1>
						<motion.p
							initial={{ opacity: 0, y: 24 }}
							animate={
								isLoaded ? { opacity: 0.8, y: 0 } : { opacity: 0, y: 24 }
							}
							transition={{
								duration: 0.6,
								ease: [0.25, 0.46, 0.45, 0.94],
								delay: 0.35,
							}}
							className="font-medium font-mono text-white text-[clamp(1.75rem,4vw,1.916rem)] sm:text-[clamp(1.25rem,2.5vw,1.916rem)] leading-[1.1] whitespace-nowrap sm:whitespace-normal text-left sm:text-center sm:mx-auto max-w-[min(95vw,45rem)]"
						>
							<span className="block sm:hidden">
								<VariableProximityText
									text="Software Engineer"
									maxSpacing={0.08}
									minSpacing={0.01}
									radius={120}
								/>
							</span>
							<span className="hidden sm:block">
								<VariableProximityText
									text="Software Engineer keen on web development and design."
									maxSpacing={0.08}
									minSpacing={0.01}
									radius={120}
								/>
							</span>
						</motion.p>
					</div>
				</section>
				<section
					className="snap-section w-full min-h-dvh relative bg-background overflow-y-auto"
					aria-labelledby="about-heading"
				>
					<h2 id="about-heading" className="sr-only">
						About Me
					</h2>
					<div className="flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-8 min-h-dvh w-full px-4 py-8 lg:py-0">
						<ProfileCard
							name="LARS"
							title="Software Engineer @ Shopware"
							handle="larskemper"
							status="GitHub"
							contactText="Visit me"
							avatarUrl={meImageUrl}
							miniAvatarUrl={meGithubImageUrl}
							showUserInfo={true}
							enableTilt={true}
							enableMobileTilt={true}
							onContactClick={() =>
								window.open('https://github.com/larskemper', '_blank')
							}
						/>
						<TerminalCard className="hidden lg:block" />
					</div>
					<div
						className="absolute bottom-0 left-0 w-full overflow-hidden pointer-events-none z-10"
						style={{ height: '50dvh' }}
					>
						<div
							className="absolute bottom-0 left-1/2 w-full text-center"
							style={{ transform: 'translate(-50%, 45%)' }}
						>
							<span
								className="font-bold text-primary/90 leading-[0.4] font-sans text-[clamp(5rem,12vw,10.565rem)] sm:text-[clamp(3rem,8vw,10.565rem)] sm:leading-none whitespace-nowrap select-none"
								aria-hidden="true"
							>
								larskemper
							</span>
						</div>
					</div>
				</section>
				<motion.nav
					initial={{ opacity: 0, x: -16 }}
					animate={isLoaded ? { opacity: 1, x: 0 } : { opacity: 0, x: -16 }}
					transition={{
						duration: 0.6,
						ease: [0.25, 0.46, 0.45, 0.94],
						delay: 0.3,
					}}
					className="fixed left-2 sm:left-4 md:left-8 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-3 sm:gap-4 pointer-events-auto"
					aria-label="Section navigation"
				>
					<button
						onClick={() => scrollToSection(0)}
						className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-200 ease-out backdrop-blur-sm cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
							activeSection === 0
								? 'bg-white scale-125 shadow-lg focus-visible:ring-white'
								: activeSection === 1
									? 'bg-primary/50 hover:bg-primary/70 hover:scale-110 active:scale-95 focus-visible:ring-primary'
									: 'bg-white/50 hover:bg-white/70 hover:scale-110 active:scale-95 focus-visible:ring-white'
						}`}
						aria-label="Go to home section"
						aria-current={activeSection === 0 ? 'page' : undefined}
					/>
					<button
						onClick={() => scrollToSection(1)}
						className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-200 ease-out backdrop-blur-sm cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
							activeSection === 1
								? 'bg-primary scale-125 shadow-lg focus-visible:ring-primary'
								: activeSection === 0
									? 'bg-white/50 hover:bg-white/70 hover:scale-110 active:scale-95 focus-visible:ring-white'
									: 'bg-primary/50 hover:bg-primary/70 hover:scale-110 active:scale-95 focus-visible:ring-primary'
						}`}
						aria-label="Go to me section"
						aria-current={activeSection === 1 ? 'page' : undefined}
					/>
				</motion.nav>
			</div>
		</>
	);
}

export default App;
